// Import sound files as modules to avoid static file serving issues
import defaultSound from '../assets/sounds/notification-default.wav';
import successSound from '../assets/sounds/notification-success.wav';
import warningSound from '../assets/sounds/notification-warning.wav';
import errorSound from '../assets/sounds/notification-error.wav';
import magicSound from '../assets/sounds/notification-magic.wav';
import pristineSound from '../assets/sounds/notification-pristine.wav';

class NotificationSoundService {
  constructor() {
    // Use imported sound modules with magical sounds
    this.sounds = {
      default: magicSound,  // Use magical sound as default
      success: pristineSound,  // Clean ding for success
      warning: warningSound,
      error: errorSound,
      info: magicSound,  // Magical sound for info
      approval: pristineSound,  // Clean ding for approvals
      system: magicSound,  // Magical sound for system notifications
      deadline: warningSound,
      magic: magicSound,  // Explicit magic type
      pristine: pristineSound,  // Explicit pristine type
    };

    this.audioCache = {};
    this.enabled = this.loadEnabledState();
    this.volume = this.loadVolume();
    this.initialized = false;
    this.audioContext = null;
    this.audioBuffers = {};
  }

  // Initialize and preload sounds
  async init() {
    if (this.initialized) return;

    try {
      // Try Web Audio API first as it's more reliable
      await this.initWebAudio();
      this.initialized = true;
    } catch (error) {
      console.warn('Web Audio API failed, falling back to HTML5 Audio:', error);
      // Fallback to HTML5 Audio
      try {
        await this.preloadSounds();
        this.initialized = true;
      } catch (fallbackError) {
        console.error('Failed to initialize notification sounds:', fallbackError);
      }
    }
  }

  // Initialize Web Audio API
  async initWebAudio() {
    if (!window.AudioContext && !window.webkitAudioContext) {
      throw new Error('Web Audio API not supported');
    }

    this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

    // Resume context if suspended (due to autoplay policies)
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    console.log('Loading sounds via Web Audio API...');

    // Load all sounds as audio buffers
    const loadPromises = Object.entries(this.sounds).map(async ([key, url]) => {
      try {
        console.log(`Fetching audio buffer for ${key} from ${url}`);
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
        this.audioBuffers[key] = audioBuffer;
        console.log(`Successfully loaded ${key} via Web Audio API`);
      } catch (error) {
        console.error(`Failed to load ${key} via Web Audio API:`, error);
        // Don't throw, allow other sounds to load
      }
    });

    await Promise.all(loadPromises);

    if (Object.keys(this.audioBuffers).length === 0) {
      throw new Error('No sounds could be loaded via Web Audio API');
    }

    console.log('Web Audio API initialization complete:', Object.keys(this.audioBuffers));
  }

  // Preload all sound files (HTML5 Audio fallback)
  async preloadSounds() {
    console.log('Preloading notification sounds from imported modules...');
    const loadPromises = Object.entries(this.sounds).map(([key, soundUrl]) => {
      return new Promise((resolve) => {
        console.log(`Loading ${key} from: ${soundUrl}`);
        const audio = new Audio(soundUrl);
        audio.volume = this.volume;
        audio.preload = 'auto';

        // Set a timeout to resolve even if the event doesn't fire
        const timeout = setTimeout(() => {
          console.log(`Sound ${key} load timeout, using fallback`);
          this.audioCache[key] = audio;
          resolve();
        }, 3000);

        audio.addEventListener('canplaythrough', () => {
          clearTimeout(timeout);
          this.audioCache[key] = audio;
          console.log(`Sound ${key} loaded successfully`);
          resolve();
        }, { once: true });

        audio.addEventListener('error', (e) => {
          clearTimeout(timeout);
          console.warn(`Failed to load sound ${key}:`, e);
          resolve(); // Don't fail entirely if one sound fails
        }, { once: true });

        // Start loading
        audio.load();
      });
    });

    await Promise.all(loadPromises);
    console.log('Sound preloading complete:', Object.keys(this.audioCache));
  }

  // Play a notification sound
  async playSound(type = 'default') {
    console.log(`playSound called with type: ${type}, enabled: ${this.enabled}, initialized: ${this.initialized}`);

    // Check if sounds are enabled
    if (!this.enabled) {
      console.log('Notification sounds are disabled');
      return;
    }

    // Initialize if not already done
    if (!this.initialized) {
      console.log('Initializing notification sounds on demand...');
      await this.init();
    }

    // Ensure we have a valid type - fallback to 'default' if type doesn't exist
    const soundType = this.sounds[type] ? type : 'default';
    if (type !== soundType) {
      console.log(`Sound type '${type}' not found, falling back to 'default'`);
    }

    try {
      // Try Web Audio API first
      if (this.audioContext && this.audioBuffers[soundType]) {
        await this.playViaWebAudio(soundType);
        return;
      }

      // Fallback to HTML5 Audio
      await this.playViaHTMLAudio(soundType);
    } catch (error) {
      // Handle autoplay restrictions
      if (error.name === 'NotAllowedError') {
        console.log('Autoplay blocked. User interaction required first.');
        this.handleAutoplayRestriction();
      } else {
        console.error('Error playing notification sound:', error);
      }
    }
  }

  // Play sound using Web Audio API
  async playViaWebAudio(type) {
    const buffer = this.audioBuffers[type] || this.audioBuffers.default;

    if (!buffer) {
      console.warn(`No audio buffer for type: ${type}`);
      return;
    }

    // Resume context if needed
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    // Create source and gain nodes
    const source = this.audioContext.createBufferSource();
    const gainNode = this.audioContext.createGain();

    // Set buffer and volume
    source.buffer = buffer;
    gainNode.gain.value = this.volume;

    // Connect nodes
    source.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    // Play the sound
    source.start(0);
    console.log(`Played ${type} sound via Web Audio API`);
  }

  // Play sound using HTML5 Audio (fallback)
  async playViaHTMLAudio(type) {
    // If still no cached audio, try loading it directly
    let audio = this.audioCache[type] || this.audioCache.default;

    if (!audio) {
      console.log(`Loading sound directly for type: ${type}`);
      const soundPath = this.sounds[type] || this.sounds.default;
      console.log(`Attempting to load sound from: ${soundPath}`);

      // Try creating audio element with explicit CORS settings
      audio = new Audio();
      audio.crossOrigin = 'anonymous';
      audio.src = soundPath;
      audio.volume = this.volume;

      // Wait for the audio to be loadable before caching
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.warn('Audio load timeout');
          reject(new Error('Audio load timeout'));
        }, 5000);

        audio.addEventListener('canplaythrough', () => {
          clearTimeout(timeout);
          resolve();
        }, { once: true });

        audio.addEventListener('error', (e) => {
          clearTimeout(timeout);
          console.error(`Failed to load audio from ${soundPath}:`, e);
          reject(e);
        }, { once: true });

        audio.load();
      });

      this.audioCache[type] = audio;
    }

    // Clone the audio to allow overlapping sounds
    const audioClone = audio.cloneNode();
    audioClone.volume = this.volume;

    // Play the sound
    await audioClone.play();

    // Clean up after playing
    audioClone.addEventListener('ended', () => {
      audioClone.remove();
    });
  }

  // Handle browser autoplay restrictions
  handleAutoplayRestriction() {
    // Create a one-time click handler to enable sounds
    const enableSounds = async () => {
      try {
        // Resume Web Audio context if it exists
        if (this.audioContext && this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
          console.log('Web Audio context resumed');
        }

        // Play a silent sound to unlock HTML5 audio
        const silentAudio = new Audio();
        silentAudio.volume = 0;
        await silentAudio.play();
        console.log('Audio context unlocked');
      } catch (error) {
        console.error('Error unlocking audio:', error);
      }
    };

    document.addEventListener('click', enableSounds, { once: true });
  }

  // Enable/disable notification sounds
  setEnabled(enabled) {
    this.enabled = enabled;
    localStorage.setItem('notificationSoundEnabled', JSON.stringify(enabled));
  }

  // Get enabled state
  isEnabled() {
    return this.enabled;
  }

  // Set volume (0.0 to 1.0)
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    localStorage.setItem('notificationSoundVolume', this.volume.toString());

    // Update volume for all cached audio elements
    Object.values(this.audioCache).forEach(audio => {
      audio.volume = this.volume;
    });
  }

  // Get current volume
  getVolume() {
    return this.volume;
  }

  // Load enabled state from localStorage
  loadEnabledState() {
    const stored = localStorage.getItem('notificationSoundEnabled');
    return stored !== null ? JSON.parse(stored) : true;
  }

  // Load volume from localStorage
  loadVolume() {
    const stored = localStorage.getItem('notificationSoundVolume');
    return stored !== null ? parseFloat(stored) : 0.5;
  }

  // Test a specific sound type
  async testSound(type = 'default') {
    const originalEnabled = this.enabled;
    this.enabled = true; // Temporarily enable for testing

    try {
      await this.playSound(type);
    } finally {
      this.enabled = originalEnabled;
    }
  }

  // Get available sound types
  getSoundTypes() {
    return Object.keys(this.sounds);
  }
}

// Create singleton instance
const notificationSoundService = new NotificationSoundService();

// Auto-initialize when the module loads
if (typeof window !== 'undefined') {
  // Initialize after a small delay to ensure DOM is ready
  setTimeout(() => {
    notificationSoundService.init().catch(err => {
      console.warn('Failed to auto-initialize notification sounds:', err);
    });
  }, 100);
}

export default notificationSoundService;